import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Copy, DownloadSimple, CheckCircle, DeviceMobile } from "@phosphor-icons/react";
import api, { fileUrl } from "../lib/api";
import { useApp } from "../context/AppContext";
import { PlatformChip } from "../lib/meta";
import { Explainer, Jargon, WarmEmptyState } from "../components/friendly";

export default function Queue() {
  const { activeClientId, activeClient } = useApp();
  const [drafts, setDrafts] = useState([]);

  const load = useCallback(() => {
    if (activeClientId) api.get(`/drafts?client_id=${activeClientId}&status=approved`).then((r) => setDrafts(r.data));
  }, [activeClientId]);
  useEffect(load, [load]);

  const copy = async (text) => {
    await navigator.clipboard.writeText(text);
    toast.success("Caption copied — paste it in the app");
  };

  const markPosted = async (id) => {
    await api.post(`/drafts/${id}/mark-posted`, {});
    toast.success("Marked as posted 🎉");
    load();
  };

  return (
    <div className="space-y-6 fade-up max-w-xl" data-testid="queue-page">
      <div>
        <h1 className="font-display font-bold text-3xl tracking-tight">Ready-to-Post</h1>
        <p className="text-sm text-slate-500 mt-1">Approved content for <b>{activeClient?.name}</b>. Copy, download, <Jargon tip="You publish the post yourself inside each app (Instagram, LinkedIn…). Celebre prepares everything but never posts on your behalf.">post natively</Jargon>, mark done.</p>
      </div>
      <Explainer id="queue">Approved posts land here packaged for posting: copy the caption, download the media, then post it yourself in each app. Celebre never auto-publishes — open this page on your phone for the smoothest handoff.</Explainer>

      <div className="space-y-4">
        {drafts.map((d) => (
          <div key={d.draft_id} data-testid={`queue-card-${d.draft_id}`} className="bg-white border border-slate-200/80 rounded-2xl p-5 card-float">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <PlatformChip platform={d.platform} />
                {d.ai_generated && <span className="text-[10px] font-bold uppercase tracking-widest text-[hsl(9,55%,52%)] bg-[hsl(9,69%,62%)]/8 px-1.5 py-0.5 rounded">AI-assisted</span>}
              </div>
              {d.scheduled_date && <span className="text-[11px] text-slate-400 font-medium">{d.scheduled_date}</span>}
            </div>
            {d.image_url && <img src={fileUrl(d.image_url)} alt="" className="mt-3 rounded-xl border border-slate-100 w-full max-h-72 object-cover" />}
            <p className="mt-3 text-[13px] text-slate-700 whitespace-pre-wrap leading-relaxed">{d.caption}</p>
            <div className="mt-4 grid grid-cols-3 gap-2">
              <button data-testid={`copy-caption-${d.draft_id}`} onClick={() => copy(d.caption)}
                className="flex flex-col items-center gap-1 py-3 rounded-xl bg-slate-50 border border-slate-200 text-[12px] font-semibold text-slate-700 hover:bg-slate-100 transition-colors">
                <Copy size={18} weight="duotone" /> Copy
              </button>
              {d.image_url ? (
                <a data-testid={`download-media-${d.draft_id}`} href={fileUrl(d.image_url)} download target="_blank" rel="noreferrer"
                  className="flex flex-col items-center gap-1 py-3 rounded-xl bg-slate-50 border border-slate-200 text-[12px] font-semibold text-slate-700 hover:bg-slate-100 transition-colors">
                  <DownloadSimple size={18} weight="duotone" /> Media
                </a>
              ) : (
                <div className="flex flex-col items-center gap-1 py-3 rounded-xl bg-slate-50/50 border border-slate-100 text-[12px] text-slate-300"><DownloadSimple size={18} /> No media</div>
              )}
              <button data-testid={`mark-posted-${d.draft_id}`} onClick={() => markPosted(d.draft_id)}
                className="flex flex-col items-center gap-1 py-3 rounded-xl bg-emerald-600 text-white text-[12px] font-semibold hover:bg-emerald-500 transition-colors">
                <CheckCircle size={18} weight="fill" /> Posted
              </button>
            </div>
          </div>
        ))}
        {!drafts.length && <WarmEmptyState icon={DeviceMobile} title="Nothing ready to post yet" what="Once a draft is approved in the Approval Inbox, it lands here — packaged so you can copy the caption, grab the media, and post it natively in seconds." />}
      </div>
    </div>
  );
}
